import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { serviceLocationFaqData, type ServiceLocationFAQ } from "@/data/serviceLocationFaqData";
import FAQSchema from "@/components/FAQSchema";

interface ServiceLocationFAQSectionProps {
  serviceSlug: string;
  locationSlug: string;
  serviceName: string;
  locationName: string;
}

export default function ServiceLocationFAQSection({ 
  serviceSlug, 
  locationSlug, 
  serviceName, 
  locationName 
}: ServiceLocationFAQSectionProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  
  const faqs = serviceLocationFaqData[serviceSlug]?.[locationSlug] || [];

  if (!faqs.length) return null;

  return (
    <>
      {/* FAQ Schema for SEO - Only one per page */}
      <FAQSchema faqs={faqs} />
      
      {/* FAQ Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container px-4">
          <div className="text-center mb-12">
            <p className="text-[#1e3a8a] font-semibold mb-2 text-sm tracking-wider">
              FREQUENTLY ASKED QUESTIONS
            </p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0a1628] mb-4">
              {serviceName} in {locationName} FAQ
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Common questions about {serviceName.toLowerCase()} services in {locationName}.
            </p>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg border border-gray-200 hover:border-[#4caf30]/30 transition-colors"
              >
                <button
                  onClick={() =>
                    setExpandedIndex(expandedIndex === index ? null : index)
                  }
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-left font-semibold text-[#0a1628] text-sm md:text-base">
                    {faq.question}
                  </h3>
                  <ChevronDown
                    className={`w-5 h-5 text-[#4caf30] flex-shrink-0 ml-4 transition-transform ${
                      expandedIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {expandedIndex === index && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
